import { useEffect } from "react";

import dayjs from "dayjs";

import AlertModal from "@/components/Modal/AlertModal";
import useModal from "@/hooks/useModal";

interface DropdownTimePickerProps {
  selectedDate: Date;
  selectedTimes: number[];
  onChange: (newSelectedTimes: number[]) => void;
  limitTime?: number;
}

const timeOptions = (start: number, end: number) => {
  const options: { value: number; label: string }[] = [];
  for (let h = start; h <= end; h++) {
    options.push({ value: h, label: `${String(h).padStart(2, "0")}:00` });
    if (h < end) {
      options.push({ value: h + 0.5, label: `${String(h).padStart(2, "0")}:30` });
    }
  }
  return options;
};

const formatDuration = (hours: number) => {
  if (!hours || hours <= 0) return "";
  const h = Math.floor(hours);
  const m = (hours - h) * 60;
  if (h > 0 && m > 0) return `${h}시간 ${m}분`;
  if (h > 0) return `${h}시간`;
  return `${m}분`;
};

const DropdownTimePicker = ({
  selectedDate,
  selectedTimes,
  onChange,
  limitTime,
}: DropdownTimePickerProps) => {
  const [startTime, endTime] = selectedTimes;
  const alertModal = useModal();

  const getStartTime = () => {
    const isToday = dayjs(selectedDate).isSame(dayjs(), "day");
    return isToday ? Math.max(dayjs().hour() + 1, 6) : 6;
  };

  const handleChangeStartTime = (time: number) => {
    const duration = endTime - startTime;
    const newEndTime = time + duration;
    onChange([time, newEndTime > 24 ? 24 : newEndTime]);
  };

  const handleChangeEndTime = (time: number) => {
    if (limitTime && time - startTime > limitTime) {
      alertModal.onOpen();
      return;
    }
    onChange([startTime, time]);
  };

  useEffect(() => {
    onChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const startOptions = timeOptions(getStartTime(), 23);
  const maxEnd = limitTime ? Math.min(startTime + limitTime, 24) : 24;
  const endOptions = startTime
    ? timeOptions(Math.ceil(startTime + 0.5), maxEnd).filter(o => o.value > startTime && o.value <= maxEnd)
    : [];

  const duration = startTime && endTime ? endTime - startTime : 0;

  return (
    <div className="mt-4 flex flex-col gap-4">
      {/* Start time */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-gray-500">시작 시간</label>
        <select
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/30"
          value={startTime ?? ""}
          onChange={e => handleChangeStartTime(Number(e.target.value))}
        >
          <option value="" disabled>
            시작 시간 선택
          </option>
          {startOptions.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* End time */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-gray-500">종료 시간</label>
        <select
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50 disabled:text-gray-400"
          value={endTime ?? ""}
          onChange={e => handleChangeEndTime(Number(e.target.value))}
          disabled={!startTime}
        >
          <option value="" disabled>
            {startTime ? "종료 시간 선택" : "시작 시간을 먼저 선택하세요"}
          </option>
          {endOptions.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Duration badge */}
      {duration > 0 && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {formatDuration(duration)}
          </span>
          {limitTime && (
            <span className="text-xs text-gray-400">
              (최대 {limitTime}시간)
            </span>
          )}
        </div>
      )}

      {alertModal.isOpen && (
        <AlertModal
          onClose={alertModal.onClose}
        >{`${limitTime}시간까지 선택 가능합니다.`}</AlertModal>
      )}
    </div>
  );
};

export default DropdownTimePicker;
